/** Strip to digits; keep at most MMDDYYYY (8). */
export function dateDigits(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 8);
}

export type DateDigitSections = {
  month: string;
  day: string;
  year: string;
};

export function sectionsFromMasked(
  value: string | null | undefined,
): DateDigitSections {
  const raw = String(value ?? '');
  // Digits-only (paste / progressive entry): pack into MM DD YYYY slots.
  if (!raw.includes('/')) {
    const digits = dateDigits(raw);
    return {
      month: digits.slice(0, 2),
      day: digits.slice(2, 4),
      year: digits.slice(4, 8),
    };
  }
  // Slash-separated: keep sections independent, but spill overflow digits
  // forward (e.g. typing past DD into YYYY → `07/142` becomes `07/14/2`).
  const parts = raw.split('/');
  const monthRaw = (parts[0] ?? '').replace(/\D/g, '');
  const dayRaw = (parts[1] ?? '').replace(/\D/g, '');
  const yearRaw = (parts[2] ?? '').replace(/\D/g, '');

  const month = monthRaw.slice(0, 2);
  const dayCombined = monthRaw.slice(2) + dayRaw;
  const day = dayCombined.slice(0, 2);
  const year = (dayCombined.slice(2) + yearRaw).slice(0, 4);

  return { month, day, year };
}

/** Format sections without packing later digits into earlier empty slots. */
export function formatDateSections(sections: DateDigitSections): string {
  const month = sections.month.slice(0, 2);
  const day = sections.day.slice(0, 2);
  const year = sections.year.slice(0, 4);

  if (!month && !day && !year) {
    return '';
  }
  if (!day && !year) {
    return month;
  }
  if (!year) {
    return `${month}/${day}`;
  }
  return `${month}/${day}/${year}`;
}

/** Progressive US mask: `MM/DD/YYYY` (packs digits; used for paste/initial format). */
export function formatDateMask(value: string | null | undefined): string {
  return formatDateSections(sectionsFromMasked(value));
}

export function digitCountBefore(value: string, caret: number): number {
  return value.slice(0, Math.max(0, caret)).replace(/\D/g, '').length;
}

/** Map a digit index (0..8) to a caret position in the masked string. */
export function caretFromDigitCount(
  masked: string,
  digitCount: number,
): number {
  if (digitCount <= 0) {
    return 0;
  }
  let seen = 0;
  for (let i = 0; i < masked.length; i += 1) {
    if (/\d/.test(masked[i]!)) {
      seen += 1;
      if (seen === digitCount) {
        return i + 1;
      }
    }
  }
  return masked.length;
}

function deleteDigitInSections(
  sections: DateDigitSections,
  digitIndex: number,
): DateDigitSections {
  const next = {
    month: sections.month,
    day: sections.day,
    year: sections.year,
  };
  if (digitIndex < 2) {
    const i = digitIndex;
    if (i < 0 || i >= next.month.length) {
      return next;
    }
    next.month = next.month.slice(0, i) + next.month.slice(i + 1);
    return next;
  }
  if (digitIndex < 4) {
    const i = digitIndex - 2;
    if (i < 0 || i >= next.day.length) {
      return next;
    }
    next.day = next.day.slice(0, i) + next.day.slice(i + 1);
    return next;
  }
  const i = digitIndex - 4;
  if (i < 0 || i >= next.year.length) {
    return next;
  }
  next.year = next.year.slice(0, i) + next.year.slice(i + 1);
  return next;
}

export type DateMaskEditResult = {
  masked: string;
  caret: number;
};

/**
 * Apply a browser input edit while keeping month/day/year sections independent
 * (so backspacing the month does not pull day digits left) and restoring caret.
 */
export function applyDateMaskEdit({
  previous,
  nextRaw,
  nextCaret,
}: {
  previous: string;
  nextRaw: string;
  nextCaret: number;
}): DateMaskEditResult {
  const prevSections = sectionsFromMasked(previous);
  const prevDigits = `${prevSections.month}${prevSections.day}${prevSections.year}`;
  const nextSections = sectionsFromMasked(nextRaw);
  const nextDigitsFromSections = `${nextSections.month}${nextSections.day}${nextSections.year}`;

  // Separator-only edit (e.g. backspace on `/`) — same digit payload, keep sections.
  if (nextDigitsFromSections.length === prevDigits.length) {
    const masked = formatDateSections(prevSections);
    return {
      masked,
      caret: caretFromDigitCount(masked, digitCountBefore(nextRaw, nextCaret)),
    };
  }

  // Prefer slash-aware parsing whenever the browser value still has separators,
  // or is empty — this preserves `0/25/2027` instead of packing to `02/52/027`.
  if (nextRaw === '' || nextRaw.includes('/')) {
    const masked = formatDateSections(nextSections);
    return {
      masked,
      caret: caretFromDigitCount(masked, digitCountBefore(nextRaw, nextCaret)),
    };
  }

  // Digits-only entry/paste (no slashes yet).
  const masked = formatDateMask(nextRaw);
  return {
    masked,
    caret: caretFromDigitCount(masked, digitCountBefore(nextRaw, nextCaret)),
  };
}

/**
 * Backspace when the character before the caret is `/`:
 * delete the digit before that slash instead of only removing the separator.
 */
export function backspaceDateMask(
  previous: string,
  caret: number,
): DateMaskEditResult | null {
  if (caret <= 0) {
    return null;
  }
  if (previous[caret - 1] !== '/') {
    return null;
  }
  const digitIndex = digitCountBefore(previous, caret - 1) - 1;
  if (digitIndex < 0) {
    return {
      masked: previous,
      caret: caret - 1,
    };
  }
  const sections = deleteDigitInSections(
    sectionsFromMasked(previous),
    digitIndex,
  );
  const masked = formatDateSections(sections);
  return {
    masked,
    caret: caretFromDigitCount(masked, digitIndex),
  };
}

export function isDateInputComplete(value: string | null | undefined): boolean {
  return dateDigits(value).length === 8;
}

/** True when Y/M/D form a real calendar day (rejects Feb 31, etc.). */
export function isValidCalendarDate(
  year: number,
  month: number,
  day: number,
): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return false;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Parse MM/DD/YYYY (or digit-only) to ISO YYYY-MM-DD when complete and valid.
 * Incomplete or invalid calendar dates return null.
 */
export function parseUsDateToIso(
  value: string | null | undefined,
): string | null {
  const digits = dateDigits(value);
  if (digits.length !== 8) {
    return null;
  }
  const month = Number(digits.slice(0, 2));
  const day = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  if (!isValidCalendarDate(year, month, day)) {
    return null;
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Complete but not a real calendar day (e.g. 02/31/2026). */
export function isInvalidCompleteDate(
  value: string | null | undefined,
): boolean {
  return isDateInputComplete(value) && parseUsDateToIso(value) == null;
}

/** Format an ISO YYYY-MM-DD (or epoch ms) as MM/DD/YYYY. */
export function isoToUsDateMask(
  isoOrMs: string | number | null | undefined,
): string {
  if (isoOrMs == null || isoOrMs === '') {
    return '';
  }
  if (typeof isoOrMs === 'number') {
    const d = new Date(isoOrMs);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return formatDateMask(
      `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}${d.getFullYear()}`,
    );
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoOrMs);
  if (!match) {
    return formatDateMask(isoOrMs);
  }
  const [, year, month, day] = match;
  return formatDateMask(`${month}${day}${year}`);
}

export const INVALID_DATE_MESSAGE = 'Enter a valid date.';
